import React from 'react';
import { Link } from 'react-router-dom';
import Carousel from '../../components/Carousel';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import './ProjectPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2014-02-13', displayDate: 'February 13, 2014' };

const carouselSlides = [
  {
    backgroundImage: '/images/banner_planetlab.png',
    content: (
      <>
        <p>The software that makes it easy to manage your PlanetLab experiments.</p>
        <p>
          The PlanetLab Manager is a desktop application that allows you to configure the PlanetLab site, nodes and
          slices for your experiments. Using the integrated secure shell (SSH) console you can run commands directly on
          the PlanetLab nodes.
        </p>
        <p>
          <a href="#download" className="btn btn--primary" role="button">
            Download
          </a>
          {' '}
          <a href="#howto" className="btn btn--primary" role="button">
            How It Works
          </a>
        </p>
      </>
    ),
  },
];

const PlanetLabPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  return (
    <>
      <Carousel slides={carouselSlides} />
      <div className="project-page" id="top">
        <h1 className="title">PlanetLab Manager</h1>
        <p>The software that makes it easy to manage your PlanetLab experiments.</p>
      <p>
        The PlanetLab Manager is a desktop application that allows you to configure the PlanetLab site, nodes and slices
        for your experiments. Using the integrated secure shell (SSH) console you can run commands directly on the
        PlanetLab nodes.
      </p>

      <div id="download" className="project-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-lg)' }}>
        <div>
          <h1>How Does It Work?</h1>
          <p>
            Have you ever run distributed Internet experiments using PlanetLab? The PlanetLab Manager uses the PlanetLab
            API to help you setup and manage your PlanetLab experiments from a single desktop application.
          </p>
        </div>
        <div>
          <h1>What Do You Need?</h1>
          <p>
            To use the PlanetLab Manager, you need a valid PlanetLab account. The software requires Microsoft Windows,
            version XP or newer, and .NET Framework 4.0.
          </p>
        </div>
        <div>
          <h1>Try It Yourself!</h1>
          <p>
            <a
              href="/download/PlanetLabManager_1_0_beta.exe"
              className="button button-danger"
              style={{
                display: 'inline-block',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'var(--color-accent)',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
                marginBottom: 'var(--spacing-sm)',
              }}
            >
              Download Windows Installer
            </a>
          </p>
          <p>MSI Windows Installer Executable / 2.50 MB</p>
          <p>
            <a
              href="/download/PlanetLabManager_1_0_beta.zip"
              style={{
                display: 'inline-block',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'var(--color-accent)',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
              }}
            >
              Download ZIP Archive
            </a>
          </p>
          <p>ZIP Compressed Archive / 863 KB</p>
        </div>
      </div>

      <hr style={{ margin: 'var(--spacing-xl) 0', border: 'none', borderTop: '1px solid var(--color-border-light)' }} />

      <h1 id="howto">Configure Your Account</h1>
      <div className="project-row" style={{ flexDirection: 'row-reverse', gap: 'var(--spacing-xl)' }}>
        <img
          src="/projects/inetanalytics/planetlab/PlmAccount.png"
          alt="Configure Your PlanetLab Account."
          style={{ maxWidth: '60%', flex: '1' }}
        />
        <div style={{ flex: '1' }}>
          <p>
            You begin using the PlanetLab Manager by configuring your account. You need to be a member of a university or
            research institution currently hosting a PlanetLab site.
          </p>
          <p>
            Once configured, the PlanetLab Manager will save these credentials on your computer. The program stores your
            username and password in encrypted form.
          </p>
        </div>
      </div>

      <hr style={{ margin: 'var(--spacing-xl) 0', border: 'none', borderTop: '1px solid var(--color-border-light)' }} />

      <h1>Visualize the PlanetLab Slices</h1>
      <div className="project-row" style={{ flexDirection: 'row', gap: 'var(--spacing-xl)' }}>
        <img
          src="/projects/inetanalytics/planetlab/PlmSites.png"
          alt="View the PlanetLab Sites."
          style={{ maxWidth: '60%', flex: '1' }}
        />
        <div style={{ flex: '1' }}>
          <p>
            This view illustrates the geographical location of the PlanetLab sites. It helps you plan your experiment,
            especially for research where the distribution of computing nodes is of particular interest.
          </p>
        </div>
      </div>

      <hr style={{ margin: 'var(--spacing-xl) 0', border: 'none', borderTop: '1px solid var(--color-border-light)' }} />

      <h1>Browse the PlanetLab Nodes</h1>
      <div className="project-row" style={{ flexDirection: 'row-reverse', gap: 'var(--spacing-xl)' }}>
        <img
          src="/projects/inetanalytics/planetlab/PlmNodes.png"
          alt="View the PlanetLab Nodes."
          style={{ maxWidth: '60%', flex: '1' }}
        />
        <div style={{ flex: '1' }}>
          <p>
            When you are only interested to deploy your experiment to a distributed test-bed, use the nodes view that
            enables you to quickly find available PlanetLab nodes.
          </p>
        </div>
      </div>

      <hr style={{ margin: 'var(--spacing-xl) 0', border: 'none', borderTop: '1px solid var(--color-border-light)' }} />

      <h1>Organize Your Slices</h1>
      <div className="project-row" style={{ flexDirection: 'row', gap: 'var(--spacing-xl)' }}>
        <img
          src="/projects/inetanalytics/planetlab/PlmSlices.png"
          alt="View the PlanetLab Slices."
          style={{ maxWidth: '60%', flex: '1' }}
        />
        <div style={{ flex: '1' }}>
          <p>
            A slice is the gateway to deploying a PlanetLab experiment. Once setup by your principal investigator (PI),
            you can add it to the PlanetLab nodes, giving you the necessary permissions to connect and execute programs.
          </p>
        </div>
      </div>

      <p style={{ marginTop: 'var(--spacing-lg)' }}>
        <Link to="/projects/inetanalytics">← Internet Analytics</Link>
      </p>
      <p className="update">Last updated: February 13, 2014</p>
      </div>
    </>
  );
};

export default PlanetLabPage;
